/* ============================================
   AKI — POPUPS
   Modal shell, email capture (timer + scroll depth),
   post-add-to-cart upsell, social proof toast.

   Every popup respects a frequency cap stored in
   localStorage so a returning visitor is not nagged.
   ============================================ */
(function () {
  'use strict';

  var STORE_PREFIX = 'aki:popup:';

  /* ------------------------------------------
     Analytics — ein benanntes Event je Popup-Zustand.
     Übertragen werden nur Zustände, nie Inhalte: die
     eingegebene Adresse verlässt das Formular nicht.
     ------------------------------------------ */
  function track(event, detail) {
    var payload = { event: event };
    if (detail) {
      Object.keys(detail).forEach(function (k) { payload[k] = detail[k]; });
    }
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(payload);
    } catch (e) { /* kein dataLayer — kein Grund, das Popup zu blockieren */ }
    document.dispatchEvent(new CustomEvent('aki:track', { detail: payload }));
  }

  /* ------------------------------------------
     Frequency memory

     Drei getrennte Sperren:
       • dismissedForever — eingetragen, nie wieder zeigen
       • days             — nach dem Schliessen N Tage pausieren
       • session          — höchstens einmal pro Sitzung
     ------------------------------------------ */
  var Memory = {
    read: function (key) {
      try { return JSON.parse(window.localStorage.getItem(STORE_PREFIX + key) || 'null'); }
      catch (e) { return null; }
    },
    write: function (key, value) {
      try { window.localStorage.setItem(STORE_PREFIX + key, JSON.stringify(value)); }
      catch (e) { /* private mode — popup simply shows again next visit */ }
    },
    seenThisSession: function (key) {
      try { return !!window.sessionStorage.getItem(STORE_PREFIX + key); }
      catch (e) { return false; }
    },
    markSession: function (key) {
      try { window.sessionStorage.setItem(STORE_PREFIX + key, '1'); } catch (e) {}
    },
    isSuppressed: function (key, days) {
      if (this.seenThisSession(key)) return true;
      var rec = this.read(key);
      if (!rec || !rec.at) return false;
      if (rec.dismissedForever) return true;
      if (!days) return false;
      return (Date.now() - rec.at) < days * 86400000;
    },
    suppress: function (key, forever) {
      /*
        Eine dauerhafte Sperre darf nie wieder aufgehoben werden.
        Sonst passiert genau das hier: Nach erfolgreicher Eintragung wird
        `dismissedForever` gesetzt, die Bestätigung öffnet sich, und der
        Klick auf „Weiterlesen“ läuft durch close() erneut in diese
        Funktion — mit forever = false. Damit wäre die Eintragung wieder
        vergessen und das Popup käme nach Ablauf der Frist zurück.
      */
      var existing = this.read(key);
      var permanent = !!forever || (existing && existing.dismissedForever === true);
      this.write(key, { at: Date.now(), dismissedForever: permanent });
      this.markSession(key);
    }
  };

  /* ------------------------------------------
     Focus trap (shared with drawers)
     ------------------------------------------ */
  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function trapFocus(container, onEscape) {
    function handle(e) {
      if (e.key === 'Escape') { onEscape(); return; }
      if (e.key !== 'Tab') return;
      var items = Array.from(container.querySelectorAll(FOCUSABLE)).filter(function (el) {
        return el.offsetParent !== null;
      });
      if (!items.length) return;
      var first = items[0], last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
    document.addEventListener('keydown', handle);
    return function () { document.removeEventListener('keydown', handle); };
  }

  /* Only one popup may be on screen at a time */
  var openPopup = null;

  /* ==========================================
     <aki-modal> — base shell
     ========================================== */
  class AkiModal extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      this.panel = this.querySelector('[data-modal-panel]');
      this.storageKey = this.dataset.key || this.id;
      this.release = null;
      this.opener = null;

      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-modal-close]') || e.target.closest('[data-modal-backdrop]')) {
          e.preventDefault();
          this.close(e.target.closest('[data-modal-decline]') ? 'declined' : 'closed');
        }
      });
    }

    canOpen() {
      if (openPopup) return false;
      if (window.Shopify && window.Shopify.designMode) return true;
      var days = Number(this.dataset.frequency || 7);
      return !Memory.isSuppressed(this.storageKey, days);
    }

    open(opener) {
      if (this.hasAttribute('data-open') || openPopup) return;
      openPopup = this;
      /* Merken, wohin der Fokus zurückgeht: der auslösende Knopf, sonst
         das Element, das ihn vor dem Öffnen hatte. */
      this.opener = opener || (document.activeElement !== document.body ? document.activeElement : null);
      this.setAttribute('data-open', 'true');
      document.body.classList.add('is-locked');
      this.release = trapFocus(this.panel || this, () => this.close('closed'));

      /* Eine Sitzung, ein Popup — unabhängig davon, wie es geschlossen wird. */
      Memory.markSession(this.storageKey);

      window.requestAnimationFrame(() => {
        var focusTarget = this.querySelector('[data-modal-autofocus]') || this.querySelector('[data-modal-close]');
        if (focusTarget) focusTarget.focus();
      });

      track('aki_popup_shown', { popup_id: this.storageKey });
      this.dispatchEvent(new CustomEvent('aki:modal:open', { bubbles: true }));
    }

    close(reason) {
      if (!this.hasAttribute('data-open')) return;
      this.removeAttribute('data-open');
      document.body.classList.remove('is-locked');
      if (this.release) { this.release(); this.release = null; }
      if (openPopup === this) openPopup = null;

      /* A decline is remembered longer than an incidental close */
      Memory.suppress(this.storageKey, reason === 'declined');

      if (this.opener && document.contains(this.opener) && this.opener.focus) {
        this.opener.focus();
      }
      track('aki_popup_closed', { popup_id: this.storageKey, reason: reason || 'closed' });
      this.dispatchEvent(new CustomEvent('aki:modal:close', { bubbles: true, detail: { reason: reason } }));
    }
  }

  /* ==========================================
     <aki-email-popup>
     Öffnet nach einer Wartezeit ODER ab einer Scrolltiefe —
     was zuerst eintritt. Bewusst KEIN Exit-Intent: das feuert
     schon, wenn die Maus nur kurz nach oben aus dem Fenster
     fährt, und wirkt dann wie ein Overlay beim Laden.

     Eine Mindestwartezeit ist hart verdrahtet, damit das Popup
     auch bei einer sehr kurzen Seite nie sofort erscheint.
     ========================================== */
  var MIN_DELAY_MS = 10000;

  class AkiEmailPopup extends AkiModal {
    connectedCallback() {
      super.connectedCallback();
      if (this._emailReady) return;
      this._emailReady = true;

      /* The form just posted successfully — show the confirmation once,
         then never ask this visitor again. */
      if (this.querySelector('[data-popup-success]')) {
        Memory.suppress(this.storageKey, true);
        track('aki_popup_submitted', { popup_id: this.storageKey });
        this.open();
        return;
      }

      if (!this.canOpen()) return;

      this.delay = Math.max(Number(this.dataset.delay || 12) * 1000, MIN_DELAY_MS);
      this.scrollPct = Number(this.dataset.scroll || 50);

      this.armed = true;
      this.openedAt = Date.now();
      this.timer = window.setTimeout(() => this.trigger('timer'), this.delay);

      this.onScroll = () => {
        /* Scrolltiefe zählt erst, wenn die Seite kurz gelesen wurde.
           Sonst löst ein einzelner Wischer auf dem Handy sofort aus. */
        if (Date.now() - this.openedAt < MIN_DELAY_MS) return;
        var h = document.documentElement;
        var scrollable = h.scrollHeight - h.clientHeight;
        if (scrollable < 400) return;
        var pct = (h.scrollTop / scrollable) * 100;
        if (pct >= this.scrollPct) this.trigger('scroll');
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
    }

    trigger(source) {
      if (!this.armed || !this.canOpen()) return;
      this.armed = false;
      this.teardown();
      this.trigger_source = source;
      this.open();
    }

    open(opener) {
      super.open(opener);
      if (this.trigger_source) {
        track('aki_popup_trigger', { popup_id: this.storageKey, source: this.trigger_source });
      }
    }

    teardown() {
      if (this.timer) window.clearTimeout(this.timer);
      if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
    }

    disconnectedCallback() { this.teardown(); }
  }

  /* ==========================================
     <aki-upsell-popup>
     Fires after a successful add-to-cart.
     Adds the same product again — a "make it a stock-up"
     offer, not a competing product.
     ========================================== */
  class AkiUpsellPopup extends AkiModal {
    connectedCallback() {
      super.connectedCallback();
      if (this._upsellReady) return;
      this._upsellReady = true;

      this.acceptBtn = this.querySelector('[data-upsell-accept]');

      if (this.acceptBtn) {
        this.acceptBtn.addEventListener('click', async (e) => {
          e.preventDefault();
          var id = Number(this.acceptBtn.dataset.variantId);
          var qty = Number(this.acceptBtn.dataset.quantity) || 1;
          if (!id || !window.AkiCart) return;

          this.acceptBtn.dataset.loading = 'true';
          try {
            await window.AkiCart.add([{ id: id, quantity: qty }]);
            this.close('accepted');
            var drawer = document.getElementById('aki-cart-drawer');
            if (drawer && typeof drawer.open === 'function') drawer.open();
          } catch (err) {
            console.error('aki: upsell add failed', err);
          } finally {
            delete this.acceptBtn.dataset.loading;
          }
        });
      }

      /* Listen for the add-to-cart success broadcast */
      document.addEventListener('aki:added-to-cart', (e) => {
        if (!document.contains(this)) return;
        /* Never offer the upsell when they already bought more than one */
        var qty = e.detail && e.detail.quantity ? e.detail.quantity : 1;
        if (qty > 1) return;
        if (!this.canOpen()) {
          this.fallbackToDrawer();
          return;
        }
        this.open();
      });
    }

    fallbackToDrawer() {
      var drawer = document.getElementById('aki-cart-drawer');
      if (drawer && typeof drawer.open === 'function') drawer.open();
    }

    close(reason) {
      var wasOpen = this.hasAttribute('data-open');
      super.close(reason);
      /* Declining should still take them to the cart */
      if (wasOpen && reason !== 'accepted') this.fallbackToDrawer();
    }
  }

  /* ==========================================
     <aki-social-proof>
     Rotates real review data injected from Liquid.
     If the element has no items, nothing ever renders.
     ========================================== */
  class AkiSocialProof extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      this.items = Array.from(this.querySelectorAll('[data-proof-item]'));
      if (!this.items.length) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      this.delay = Number(this.dataset.delay || 6) * 1000;
      this.duration = Number(this.dataset.duration || 6) * 1000;
      this.gap = Number(this.dataset.gap || 12) * 1000;
      this.index = 0;

      var close = this.querySelector('[data-proof-close]');
      if (close) {
        close.addEventListener('click', () => {
          this.stop();
          this.hide();
          Memory.suppress('social-proof', true);
        });
      }

      if (Memory.isSuppressed('social-proof', 30)) return;

      this.startTimer = window.setTimeout(() => this.cycle(), this.delay);
    }

    show(i) {
      this.items.forEach(function (el, n) { el.hidden = n !== i; });
      this.setAttribute('data-open', 'true');
    }

    hide() { this.removeAttribute('data-open'); }

    cycle() {
      if (document.hidden) {
        this.nextTimer = window.setTimeout(() => this.cycle(), this.gap);
        return;
      }
      this.show(this.index);
      this.index = (this.index + 1) % this.items.length;

      this.hideTimer = window.setTimeout(() => {
        this.hide();
        this.nextTimer = window.setTimeout(() => this.cycle(), this.gap);
      }, this.duration);
    }

    stop() {
      [this.startTimer, this.hideTimer, this.nextTimer].forEach(function (t) {
        if (t) window.clearTimeout(t);
      });
    }

    disconnectedCallback() { this.stop(); }
  }

  /* ==========================================
     Registration
     ========================================== */
  function define(name, ctor) {
    if (!window.customElements.get(name)) window.customElements.define(name, ctor);
  }

  define('aki-modal', AkiModal);
  define('aki-email-popup', AkiEmailPopup);
  define('aki-upsell-popup', AkiUpsellPopup);
  define('aki-social-proof', AkiSocialProof);
})();
